import { ReactElement, memo } from "react";
import { Card } from "react-bootstrap";

import { ReactComponent as GraphArrowIncrease } from "assets/svg/graph-arrow-increase.svg";
import { ReactComponent as GraphArrowDecrease } from "assets/svg/graph-arrow-decrease.svg";

import {
    ReportItemSummaryType,
    ReportSummaryInfoSingleType,
    ReportTimeRangeType,
} from "types/Reports";

import { getTitleByTime } from "./utils/reportString";
import {
    formatValueAndUnitBasedOnTypeInReport,
    formatValuePercentInReport,
} from "./utils/reportFormat";

import { CustomSpinner } from "components/dashboard/widgets/WidgetBox";

type ReportSummaryCardProps = {
    title: string;
    itemType: ReportItemSummaryType;
    timeRangeType: ReportTimeRangeType;
    textHighlightClassName: string;

    item: ReportSummaryInfoSingleType;
    isLoading: boolean;

    unit: string;
    icon: ReactElement;
};

export default memo(function ReportSummaryCard({
    title,
    itemType,
    timeRangeType,
    textHighlightClassName,

    item,
    isLoading,

    unit,
    icon,
}: Readonly<ReportSummaryCardProps>) {
    const totalCurText = 'Total for the month';
    const totalPrevText = 'Total of previous month';
    const titleTimeText = getTitleByTime(timeRangeType);

    const totalCurrentDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, item.total, unit, false);
    const totalPreviousDisplay = formatValueAndUnitBasedOnTypeInReport(itemType, item.previousTotal, unit, false);

    let totalClasses = 'report-sc-total d-flex justify-content-between align-items-center report-sc-back';
    let percentClasses = 'd-flex align-items-center report-sc-cont-bottom';

    if (item.percent === null || item.percent === 0) {
        percentClasses = 'd-flex align-items-center report-sc-cont-bottom invisible';
        totalClasses = 'report-sc-total d-flex justify-content-between align-items-center';
    }

    let body = <section className="report-sc-loading">
        <CustomSpinner animation="border" data-testid="report-sc-loading-custom-spinner" />
    </section>;

    if (!isLoading) {
        body = <>
            <section className={totalClasses}>
                {icon}
                <div className="report-sc-total-container d-block position-relative text-right">
                    <p className="report-sc-cur">{totalCurText}</p>
                    <p className={`report-sc-total-cur ${textHighlightClassName}`}>
                        {`${totalCurrentDisplay}`}
                    </p>
                    <p className="report-sc-prev">{totalPrevText}</p>
                    <p className="report-sc-total-prev">
                        {`${totalPreviousDisplay}`}
                    </p>
                </div>
            </section>

            <section
                className={percentClasses}
            >
                {
                    item.percent !== null && item.percent > 0 ? <GraphArrowIncrease width="16px" height="16px" />
                    : <GraphArrowDecrease width="16px" height="16px" />
                }
                <span className="report-sc-pct">
                    {`${formatValuePercentInReport(item.percent ?? 0)}%`}
                </span>
                <span className="report-sc-pct-desc">
                    {`${item.percent !== null && item.percent > 0 ? 'increase' : 'decrease'} from previous month.*`}
                </span>
            </section>
        </>;
    }

    return (
        <Card className="report-sc">
            <h6 className="report-sc-title">{title}{titleTimeText}</h6>

            {body}
        </Card>
    );
});
